import * as React from 'react';
import * as ResizablePrimitive from 'react-resizable-panels';

import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '@cotera/client/app/components';

const ResizablePanelGroup = React.forwardRef<
  React.ElementRef<typeof ResizablePrimitive.PanelGroup>,
  React.ComponentPropsWithoutRef<typeof ResizablePrimitive.PanelGroup>
>(({ className, ...props }, ref) => (
  <ResizablePrimitive.PanelGroup
    ref={ref}
    data-slot="resizable-panel-group"
    className={cn(
      'flex h-full w-full data-[panel-group-direction=vertical]:flex-col',
      className
    )}
    {...props}
  />
));
ResizablePanelGroup.displayName = 'ResizablePanelGroup';

function ResizablePanel({
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.Panel>) {
  return <ResizablePrimitive.Panel data-slot="resizable-panel" {...props} />;
}

/** How long the pointer has to settle on the seam before the hint appears. */
const RESIZE_HINT_DELAY_MS = 450;

/** One gesture: what it does, then how to do it. */
const ResizeHintRow: React.FC<{ action: string; gesture: string }> = ({
  action,
  gesture,
}) => (
  <div className="flex items-baseline gap-2 whitespace-nowrap text-xs leading-5">
    <span className="font-medium text-foreground">{action}</span>
    <span className="text-muted-foreground">{gesture}</span>
  </div>
);

function ResizableHandle({
  withHandle,
  variant = 'default',
  className,
  hitAreaMargins,
  onDragging,
  onDoubleClick,
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.PanelResizeHandle> & {
  withHandle?: boolean;
  /**
   * Borderless, unmarked handle over the seam between a pane and the card
   * beside it. Wider hit area than it looks, because there is nothing to look
   * at: the seam carries no grip and no rule, the way Claude Desktop's does.
   * A grip parked there is a permanent mark down the middle of the window for a
   * gesture almost nobody makes twice, and the pane it divides is the thing
   * being read.
   */
  variant?: 'default' | 'artifact-card';
}) {
  // The unmarked seam has no affordance but the resize cursor, so the gestures
  // are taught on approach instead of advertised permanently — see `variant`.
  //
  // Position is written straight to the node rather than held in state: the
  // hint tracks the pointer down a full-height element, and re-rendering the
  // library's handle on every `pointermove` to move a two-line box is a poor
  // trade. Only visibility is state.
  const [hintVisible, setHintVisible] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const hintRef = React.useRef<HTMLDivElement | null>(null);
  const hintTimeoutRef = React.useRef<number | undefined>(undefined);
  const showsHint = variant === 'artifact-card';

  // Marks the drag on `body` for the duration, which is what the closed-hand
  // cursor in `styles.css` keys off. It has to be a global attribute and a
  // global rule: the library paints its own cursor with `*{cursor: … !important}`
  // from a stylesheet it owns, and only a more specific `!important` rule
  // outranks that — see the note beside `body[data-resize-dragging]`.
  //
  // Set from an effect rather than inside `onDragging` so the attribute is
  // removed if this handle unmounts mid-drag. A drag that ends with the panel
  // gone — collapsed past its floor, or the artifact closed under the pointer —
  // would otherwise leave every cursor in the app stuck as a fist.
  React.useEffect(() => {
    if (!isDragging) {
      return;
    }
    document.body.setAttribute('data-resize-dragging', '');
    return () => document.body.removeAttribute('data-resize-dragging');
  }, [isDragging]);

  const hideHint = React.useCallback(() => {
    if (hintTimeoutRef.current !== undefined) {
      window.clearTimeout(hintTimeoutRef.current);
      hintTimeoutRef.current = undefined;
    }
    setHintVisible(false);
  }, []);

  React.useEffect(() => hideHint, [hideHint]);

  // Placed in viewport coordinates so the pointer's own position is all it
  // takes — no measuring the full-height handle it lives in, and no ancestor's
  // `overflow` to escape. The parameter is deliberately structural: the
  // library types its element as a string union, so its `PointerEvent` has no
  // usable `currentTarget`, and asking only for what is read keeps this
  // assignable without a cast.
  const moveHintTo = (event: { clientX: number; clientY: number }): void => {
    const node = hintRef.current;
    if (node === null) {
      return;
    }
    node.style.left = `${event.clientX}px`;
    node.style.top = `${event.clientY}px`;
  };

  return (
    <ResizablePrimitive.PanelResizeHandle
      data-slot="resizable-handle"
      data-variant={variant}
      hitAreaMargins={
        variant === 'artifact-card' ? { fine: 8, coarse: 16 } : hitAreaMargins
      }
      onDragging={(dragging) => {
        // Never over the thing being resized.
        if (dragging) {
          hideHint();
        }
        setIsDragging(dragging);
        onDragging?.(dragging);
      }}
      onDoubleClick={onDoubleClick}
      {...(showsHint && {
        onPointerEnter: (event: { clientX: number; clientY: number }) => {
          moveHintTo(event);
          hintTimeoutRef.current = window.setTimeout(() => {
            setHintVisible(true);
          }, RESIZE_HINT_DELAY_MS);
        },
        onPointerMove: moveHintTo,
        onPointerLeave: hideHint,
      })}
      className={cn(
        'focus-visible:ring-ring relative flex items-center justify-center focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:outline-hidden',
        variant === 'default' &&
          'bg-border w-px after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:after:left-0 data-[panel-group-direction=vertical]:after:h-1 data-[panel-group-direction=vertical]:after:w-full data-[panel-group-direction=vertical]:after:translate-x-0 data-[panel-group-direction=vertical]:after:-translate-y-1/2 [&[data-panel-group-direction=vertical]>div]:rotate-90',
        variant === 'artifact-card' &&
          // `border-transparent` opts out of the `*` compat rule in styles.css
          // that hands every element `border-color: var(--color-border)`. The
          // seam is meant to be nothing at all, and this is the one element on
          // it — no colour of its own, inherited or otherwise.
          //
          // `w-2` matches the gutter the artifact's wrapper leaves for it
          // (`md:pl-2` in `ArtifactSidePanel`), so the handle covers the gap
          // between the panes and stops there. At `w-5` against a smaller
          // gutter it ran on under the card, which put a resize cursor over the
          // card's own left edge.
          //
          // `-mr-2` must stay equal to the width: the negative margin is what
          // gives the handle zero net layout width, so the two panels still
          // divide 100% between them and the handle floats over the boundary.
          // Change one and the split silently shifts.
          //
          // Grabbing it does not depend on those 8px — `hitAreaMargins` above
          // extends the hit area 8px each side, so the target is ~24px wide.
          'relative z-30 w-2 shrink-0 -mr-2 justify-end border-transparent bg-transparent',
        className
      )}
      {...props}
    >
      {withHandle === true && (
        <div className="bg-border z-10 flex items-center justify-center rounded-sm border py-1">
          <Icon icon="grip" className="size-3.5" />
        </div>
      )}
      {showsHint && (
        // Mounted whether or not it is showing, so `moveHintTo` has a node to
        // write to before the delay elapses and the hint appears already in the
        // right place rather than jumping there on its first move.
        <div
          ref={hintRef}
          role="tooltip"
          aria-hidden={!hintVisible}
          className={cn(
            'pointer-events-none fixed z-50 w-max -translate-x-1/2 translate-y-4 rounded-lg border border-border bg-background px-2.5 py-1.5 shadow-md transition-opacity duration-150 motion-reduce:transition-none',
            hintVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          {/* Just "Drag". Arrow keys do resize the focused handle, but this hint
              only ever opens under a pointer that has settled on the seam — so
              the audience for a keyboard gesture is the one audience that
              cannot see it, and naming it here only crowded the row that the
              pointer user came for. */}
          <ResizeHintRow action="Resize" gesture="Drag" />
          {onDoubleClick !== undefined ? (
            <ResizeHintRow action="Reset" gesture="Double-click" />
          ) : null}
        </div>
      )}
    </ResizablePrimitive.PanelResizeHandle>
  );
}

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
